import { Menu } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxesStacked,
  faCalendarDays,
  faChartColumn,
  faClipboardList,
  faFlask,
  faGaugeHigh,
  faHospital,
  faIdBadge,
  faLayerGroup,
  faListUl,
  faMoneyBillWave,
  faPalette,
  faReceipt,
  faRightLeft,
  faRuler,
  faSliders,
  faTableCellsLarge,
  faCircleInfo,
  faTruckFast,
  faUmbrellaBeach,
  faUserDoctor,
  faUserGroup,
} from '@fortawesome/free-solid-svg-icons';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getStoredUser, isAuthenticated } from '../../utilities/authentication';
import { usePageAccess } from '../../hooks/usePageAccess';
import useTranslation from '../../localization/useTranslation';
import { DOCTOR_HIDDEN_MENU_PAGES, isDoctorUser, VENDOR_HIDDEN_MENU_PAGES } from '../../utilities/doctorPortal';
import platformLogoLight from '../../images/logo/Dentris-logo-light.png';
import platformLogoDark from '../../images/logo/Dentrist-logo-dark.png';
import './SideBar.css';

const buildHiddenMenuNames = (user) => {
  const hidden = new Set([
    'formal_types',
    'units',
    'unit_types',
    'branchs',
    'assets',
    'colors',
    'formula_types',
    'delivery_centers',
    'doctors',
    'wallets',
    'activities',
    'transactions',
    'expenses',
    'expense_types',
    'delivery_types',
    'delivery_type',
    'deliverytypes',
    'delivery-types',
  ]);

  if (isDoctorUser(user)) {
    DOCTOR_HIDDEN_MENU_PAGES.forEach((name) => hidden.add(name));
  } else {
    VENDOR_HIDDEN_MENU_PAGES.forEach((name) => hidden.add(name));
  }

  return hidden;
};

const normalizePath = (path) => (path ? path.replace(/\/$/, '').toLowerCase() : '');

const iconMap = {
  dashboard: faGaugeHigh,
  assessment: faChartColumn,
  calendar: faCalendarDays,
  settings: faSliders,
  badge: faIdBadge,
  apartment: faHospital,
  groups: faUserGroup,
  shopping_cart: faClipboardList,
  receipt: faReceipt,
  category: faLayerGroup,
  palette: faPalette,
  inventory: faBoxesStacked,
  payments: faMoneyBillWave,
  medical_services: faUserDoctor,
  people: faUserGroup,
  swap_horiz: faRightLeft,
  straighten: faRuler,
  view_quilt: faTableCellsLarge,
  local_shipping: faTruckFast,
  history: faListUl,
  flask: faFlask,
  info: faCircleInfo,
  beach_access: faUmbrellaBeach,
};

const entryNameIconMap = {
  dashboard: faGaugeHigh,
  reports: faChartColumn,
  orders: faClipboardList,
  invoices: faReceipt,
  clients: faUserGroup,
  doctors: faUserDoctor,
  users: faUserGroup,
  roles: faIdBadge,
  settings: faSliders,
  'setting.lab_info': faCircleInfo,
  activities: faListUl,
  transactions: faRightLeft,
  expenses: faMoneyBillWave,
  calendar: faCalendarDays,
};

const NavIcon = ({ icon, compact = false }) => (
  <span className={`sidebar-nav-icon${compact ? ' sidebar-nav-icon--compact' : ''}`} aria-hidden="true">
    <FontAwesomeIcon icon={icon} />
  </span>
);

const SideBar = () => {
  const { theme } = useSelector(state => state.theme);
  const platformLogo = theme === 'dark' ? platformLogoDark : platformLogoLight;
  const { menu, user: pageAccessUser } = usePageAccess();
  const user = pageAccessUser ?? getStoredUser();
  const location = useLocation();
  const { t } = useTranslation();
  const [openKeys, setOpenKeys] = useState([]);

  const renderIcon = useCallback((iconName, entryName, compact = false) => {
    const resolvedIcon = iconName || (entryName === 'setting.lab_info' ? 'info' : null);
    const mapped =
      (resolvedIcon && iconMap[resolvedIcon]) ||
      (entryName && entryNameIconMap[entryName]) ||
      faListUl;

    return <NavIcon icon={mapped} compact={compact} />;
  }, []);

  const translateMenuLabel = useCallback(
    (entry) => {
      if (!entry) {
        return '';
      }

      const candidates = [];

      if (typeof entry?.meta?.translationKey === 'string') {
        candidates.push(entry.meta.translationKey);
      }

      if (typeof entry?.translation_key === 'string') {
        candidates.push(entry.translation_key);
      }

      if (typeof entry?.name === 'string' && entry.name.trim().length > 0) {
        const rawName = entry.name.trim();
        candidates.push(`sidebar.${rawName}`);
        candidates.push(`sidebar.${rawName.replace(/\./g, '_')}`);
      }

      if (typeof entry?.title === 'string' && entry.title.trim().length > 0) {
        const normalizedTitle = entry.title.trim().toLowerCase().replace(/\s+/g, '_');
        candidates.push(`sidebar.${normalizedTitle}`);
      }

      for (const key of candidates) {
        if (!key) {
          continue;
        }

        const translated = t(key);
        if (translated && translated !== key) {
          return translated;
        }
      }

      return entry?.title ?? entry?.name ?? '';
    },
    [t]
  );

  const visibleMenu = useMemo(() => {
    if (!Array.isArray(menu)) {
      return [];
    }

    const hidden = buildHiddenMenuNames(user);
    return menu.filter((entry) => !hidden.has(entry?.name));
  }, [menu, user]);

  const mapMenuEntry = useCallback((entry) => {
    const children = Array.isArray(entry?.children)
      ? entry.children.filter((child) => child?.is_menu_item !== false)
      : [];

    if (children.length > 0) {
      return {
        key: entry.name,
        icon: renderIcon(entry.icon, entry.name),
        label: translateMenuLabel(entry),
        children: children.map((child) => ({
          key: child.name,
          icon: renderIcon(child.icon, child.name, true),
          label: child.path
            ? <Link to={child.path}>{translateMenuLabel(child)}</Link>
            : translateMenuLabel(child),
        })),
      };
    }

    const label = translateMenuLabel(entry);
    return {
      key: entry.name,
      icon: renderIcon(entry.icon, entry.name),
      label: entry.path ? <Link to={entry.path}>{label}</Link> : label,
    };
  }, [translateMenuLabel, renderIcon]);

  const items = useMemo(
    () => visibleMenu.map((entry) => mapMenuEntry(entry)),
    [visibleMenu, mapMenuEntry]
  );

  const rootSubmenuKeys = useMemo(
    () => items.filter((entry) => Array.isArray(entry?.children) && entry.children.length > 0).map((entry) => entry.key),
    [items]
  );

  const { selectedKeys, submenuOpenKeys } = useMemo(() => {
    const current = normalizePath(location.pathname);

    let bestMatch = null;

    visibleMenu.forEach((entry) => {
      const entryPath = normalizePath(entry.path);
      if (entryPath && current.startsWith(entryPath)) {
        if (!bestMatch || entryPath.length > bestMatch.pathLength) {
          bestMatch = { key: entry.name, open: [], pathLength: entryPath.length };
        }
      }

      (entry.children ?? []).forEach((child) => {
        const childPath = normalizePath(child.path);
        if (!childPath || !current.startsWith(childPath)) {
          return;
        }

        if (!bestMatch || childPath.length > bestMatch.pathLength) {
          bestMatch = {
            key: child.name,
            open: [entry.name],
            pathLength: childPath.length,
          };
        }
      });
    });

    if (bestMatch) {
      return { selectedKeys: [bestMatch.key], submenuOpenKeys: bestMatch.open };
    }

    if (!current || current === '/') {
      const dashboardEntry = visibleMenu.find((entry) => entry?.name === 'dashboard');
      if (dashboardEntry) {
        return { selectedKeys: ['dashboard'], submenuOpenKeys: [] };
      }
    }

    if (current.startsWith('/client-transactions') || current.startsWith('/doctor-transactions')) {
      return { selectedKeys: ['client_transactions'], submenuOpenKeys: [] };
    }

    if (current.startsWith('/client-settings') || current.startsWith('/doctor-settings')) {
      return { selectedKeys: ['client_settings'], submenuOpenKeys: [] };
    }

    return { selectedKeys: [], submenuOpenKeys: [] };
  }, [visibleMenu, location.pathname]);

  const routeRootKey = submenuOpenKeys.find((key) => rootSubmenuKeys.includes(key)) ?? null;

  useEffect(() => {
    if (!routeRootKey) {
      return;
    }

    
    setOpenKeys((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const nested = current.filter((key) => !rootSubmenuKeys.includes(key));
      const next = [...nested, routeRootKey];
      const areSame =
        current.length === next.length && current.every((key, idx) => key === next[idx]);
      return areSame ? current : next;
    });
  }, [location.pathname, routeRootKey, rootSubmenuKeys]);

  const onOpenChange = useCallback(
    (keys) => {
      setOpenKeys((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        const latestRootKey = keys.find(
          (key) => !current.includes(key) && rootSubmenuKeys.includes(key)
        );

        const next = latestRootKey
          ? keys.filter((key) => key === latestRootKey || !rootSubmenuKeys.includes(key))
          : keys;

        const areSame =
          current.length === next.length && current.every((key, idx) => key === next[idx]);

        return areSame ? current : next;
      });
    },
    [rootSubmenuKeys]
  );

  return (
    <div className="sidebar-brand-shell">
      <div className="sidebar-brand">
        <Link to="/" className="sidebar-brand__link">
          <img
            src={platformLogo}
            alt="Dentrist"
            className="sidebar-brand__logo"
          />
        </Link>
      </div>
      {isAuthenticated() ? (
        <Menu
          mode="inline"
          theme={theme === 'dark' ? 'dark' : 'light'}
          className={`sidebar-menu ${theme === 'dark' ? 'sidebar-menu--dark' : 'sidebar-menu--light'}`}
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          items={items}
        />
      ) : (
        ''
      )}
    </div>
  );
};

export default SideBar;
